import { KINDS, PLACES, countLabel, kindFolderLabel, placeLabel, type LibraryPlace } from './libraryKinds';

/**
 * FV - the folder view of the document library.
 *
 * His words, 2026-09-08: *"like a mac almost - folders like properties or listings or proposals,
 * just a simple way to organize stuff."*
 *
 * ⭐⭐ MEASURED BEFORE DESIGNING (live, 2026-09-08): 100 files · 39 properties hold documents and
 * 30 of them hold exactly ONE · 2 deals · 1 portal · 16 filed nowhere · `master_property`,
 * `contact` and `company` hold zero. So this is a tree two levels deep - a handful of PLACES, the
 * records inside each, and the documents - and not a taxonomy. Kinds are folders too, because
 * "proposals" and "listings" are how he named them.
 *
 * ⭐⭐ EVERY FOLDER NAMES ITS CONTENTS. The DOC0c ruling: a control that says "Show all 7" reads as
 * loss; one that says "Properties · 39 folders · 69 documents" is a door. A kind nothing has been
 * filed under yet says "none yet" rather than disappearing - `brochure` and `listing_proposal`
 * are categories the generators write and nothing has filed, and hiding them would hide the
 * two folders he actually asked for.
 *
 * ⛔ Presentation only. Filing, opening and removing live on the rows, which are the SAME
 * component the list view renders - nothing DOC1b-DOC1d built exists twice.
 */

export interface FolderSummary {
  targetType: string;
  targetId: string;
  label: string;
  resolved: boolean;
  count: number;
}

export interface FoldersData {
  total: number;
  needsAHome: number;
  places: Array<{ place: LibraryPlace; documents: number; folders: FolderSummary[] }>;
  kinds: Array<{ key: string; count: number }>;
}

const FolderGlyph = ({ tone = 'gray' }: { tone?: 'gray' | 'amber' | 'red' }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className={`h-7 w-7 shrink-0 ${tone === 'amber' ? 'text-amber-500' : tone === 'red' ? 'text-primary-500' : 'text-gray-400'}`}
    fill="currentColor"
  >
    <path d="M3 6.5A2.5 2.5 0 015.5 4h4.2c.6 0 1.2.2 1.6.7l1.2 1.3h6A2.5 2.5 0 0121 8.5v9A2.5 2.5 0 0118.5 20h-13A2.5 2.5 0 013 17.5v-11z" />
  </svg>
);

const tile =
  'flex min-w-0 items-center gap-3 rounded border border-gray-200 bg-white px-3 py-2.5 text-left hover:border-gray-400 hover:bg-gray-50';

interface TileProps {
  name: string;
  detail: string;
  tone?: 'gray' | 'amber' | 'red';
  id: string;
  onClick: () => void;
}

const Tile = ({ name, detail, tone, id, onClick }: TileProps) => (
  <button type="button" data-folder-tile={id} onClick={onClick} className={tile}>
    <FolderGlyph tone={tone} />
    <span className="min-w-0">
      <span className="block truncate text-[13px] font-semibold text-gray-800">{name}</span>
      <span className="block text-[11.5px] text-gray-500">{detail}</span>
    </span>
  </button>
);

/** The top level: places, kinds, and the documents that need a home. */
export function FolderTiles({
  data, onPlace, onKind, onNeedsAHome,
}: {
  data: FoldersData;
  onPlace: (place: LibraryPlace) => void;
  onKind: (kind: string) => void;
  onNeedsAHome: () => void;
}) {
  const placeCounts = new Map(data.places.map((p) => [p.place, p]));
  const kindCounts = new Map(data.kinds.map((k) => [k.key, k.count]));
  // A kind the vocabulary does not know yet still gets a folder, after the known ones.
  const extraKinds = data.kinds.filter((k) => !KINDS.some((x) => x.key === k.key));

  return (
    <div className="space-y-4">
      <section>
        <h2 className="mb-2 text-[11.5px] font-semibold uppercase tracking-wide text-gray-400">Filed on</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {PLACES.filter((p) => p.always || placeCounts.has(p.key)).map((p) => {
            const held = placeCounts.get(p.key);
            const folders = held?.folders.length ?? 0;
            const detail = folders === 0
              ? 'none yet'
              : `${countLabel(folders, 'folder')} · ${countLabel(held?.documents ?? 0)}`;
            return (
              <Tile key={p.key} id={`place:${p.key}`} name={p.label} detail={detail} onClick={() => onPlace(p.key)} />
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-[11.5px] font-semibold uppercase tracking-wide text-gray-400">By kind</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {[...KINDS.map((k) => ({ key: k.key, label: k.label })), ...extraKinds.map((k) => ({ key: k.key, label: kindFolderLabel(k.key) }))]
            .map((k) => (
              <Tile
                key={k.key}
                id={`kind:${k.key}`}
                name={k.label}
                detail={countLabel(kindCounts.get(k.key) ?? 0)}
                onClick={() => onKind(k.key)}
              />
            ))}
        </div>
      </section>

      <section>
        {/* ⭐⭐⭐ THE 16. Visible on no other page in the app, so this folder is the most useful
            one here - and it is named for what it holds, in the colour the rows use. */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <Tile
            id="needs"
            name="Needs a home"
            detail={data.needsAHome === 0 ? 'nothing waiting' : `${countLabel(data.needsAHome)} not filed anywhere yet`}
            tone="amber"
            onClick={onNeedsAHome}
          />
        </div>
      </section>
    </div>
  );
}

/** The second level: one folder per record in a place, each with what it holds. */
export function SubfolderList({
  place, folders, onOpen,
}: {
  place: LibraryPlace;
  folders: FolderSummary[];
  onOpen: (folder: FolderSummary) => void;
}) {
  if (!folders.length) {
    return (
      <p className="mt-2 text-[12.5px] text-gray-500">
        Nothing is filed on {placeLabel(place).toLowerCase()} yet. Use "File it" on a document to put one here.
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {folders.map((f) => (
        <li key={f.targetId}>
          <button
            type="button"
            data-subfolder={f.targetId}
            onClick={() => onOpen(f)}
            className={`${tile} w-full`}
          >
            <FolderGlyph tone={f.resolved ? 'gray' : 'amber'} />
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold text-gray-800">{f.label}</span>
              <span className="block text-[11.5px] text-gray-500">{countLabel(f.count)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export interface Crumb { label: string; onClick?: () => void }

/** Documents › Properties › 10722 Arrow Rte - the way back up. */
export function Breadcrumb({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav aria-label="Folder path" className="flex flex-wrap items-center gap-1 text-[12.5px]">
      {crumbs.map((c, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <span className="text-gray-300">›</span>}
          {c.onClick ? (
            <button type="button" onClick={c.onClick} className="text-gray-600 hover:underline">{c.label}</button>
          ) : (
            <span className="font-semibold text-gray-800">{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
